// 🌀 Advanced Loading Spinner Component
// React Patterns: React.memo + PropTypes + Custom Animations

import React, { memo } from 'react';
import PropTypes from 'prop-types';

const LoadingSpinner = memo(({
  size = 'medium',
  color = 'blue',
  message = '',
  fullScreen = false,
  overlay = false,
  className = ''
}) => {
  // 🎨 Size configurations
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
    xlarge: 'h-16 w-16',
  };

  // 🎨 Color configurations
  const colorClasses = {
    blue: 'border-blue-500',
    green: 'border-green-500',
    red: 'border-red-500',
    yellow: 'border-yellow-500',
    purple: 'border-purple-500',
    gray: 'border-gray-500',
    white: 'border-white',
  };

  // 📏 Text size based on spinner size
  const textSizes = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
    xlarge: 'text-xl',
  };

  // 🎯 Spinner component
  const spinnerElement = (
    <div className={`
      ${sizeClasses[size]}
      animate-spin
      rounded-full
      border-2
      border-gray-200
      ${colorClasses[color]}
      border-t-transparent
      ${className}
    `} />
  );

  // 🎯 Content component
  const content = (
    <div className="flex flex-col items-center justify-center space-y-3">
      {spinnerElement}
      {message && (
        <p className={`text-gray-600 ${textSizes[size]} font-medium animate-pulse`}>
          {message}
        </p>
      )}
    </div>
  );

  // 🖥️ Full screen version
  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        {content}
      </div>
    );
  }

  // 🎭 Overlay version
  if (overlay) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-40">
        {content}
      </div>
    );
  }

  // 📱 Regular version
  return content;
});

LoadingSpinner.displayName = 'LoadingSpinner';

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xlarge']),
  color: PropTypes.oneOf(['blue', 'green', 'red', 'yellow', 'purple', 'gray', 'white']),
  message: PropTypes.string,
  fullScreen: PropTypes.bool,
  overlay: PropTypes.bool,
  className: PropTypes.string,
};

export default LoadingSpinner;